import { useState, useCallback, useRef, useEffect } from 'react';
import { CTA, GHOST, DROPZONE } from '@components/ui/designTokens';
import { PlusIcon } from '@heroicons/react/24/outline';
import { AddressForm } from './AddressForm';
import type { Address } from '../types/customer.types';
import { v4 as uuidv4 } from 'uuid';

interface AddressListManagerProps {
  addresses: Address[];
  onChange: (addresses: Address[]) => void;
}

export function AddressListManager({ addresses, onChange }: AddressListManagerProps) {
  const [addressList, setAddressList] = useState<Partial<Address>[]>(() => {
    if (addresses.length > 0) {
      // Ensure existing addresses have IDs
      return addresses.map(addr => ({
        ...addr,
        _id: addr._id || uuidv4()
      }));
    }
    // Create default address with unique ID for new customers
    return [
      {
        _id: uuidv4(),
        type: 'BILLING',
        is_default: true,
        country: '',
        line1: '',
      },
    ];
  });

  // Use ref to always have the latest onChange without triggering re-renders
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const handleAddAddress = () => {
    const newAddress: Partial<Address> = {
      _id: uuidv4(),
      type: 'BILLING',
      is_default: false,
      country: '',
      line1: '',
    };
    const updated = [...addressList, newAddress];
    setAddressList(updated);
    onChangeRef.current(updated as Address[]);
  };

  const handleRemoveAddress = useCallback((index: number) => {
    setAddressList(prevList => {
      if (prevList.length === 1) {
        // Don't allow removing the last address
        return prevList;
      }

      const updated = prevList.filter((_, i) => i !== index);

      // If we removed the default address, make the first one default
      const hasDefault = updated.some(addr => addr.is_default);
      if (!hasDefault && updated.length > 0) {
        updated[0].is_default = true;
      }

      onChangeRef.current(updated as Address[]);
      return updated;
    });
  }, []);

  const handleAddressChange = useCallback((index: number, updatedAddress: Partial<Address>) => {
    setAddressList(prevList => {
      const updated = [...prevList];

      // If this address is being set as default, unset all others
      if (updatedAddress.is_default) {
        updated.forEach((addr, i) => {
          if (i !== index) {
            addr.is_default = false;
          }
        });
      }

      updated[index] = updatedAddress;
      onChangeRef.current(updated as Address[]);
      return updated;
    });
  }, []);

  return (
    <div className="flex flex-col" data-testid="customer-address-list-container">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h3 className="text-[11px] font-bold uppercase tracking-[0.16em] text-secondary-400">
            Direcciones
          </h3>
          <span className="rounded-full bg-[#f4f4f4] px-2 py-px font-mono text-[11px] font-bold text-secondary-500 dark:bg-secondary-700 dark:text-secondary-300">
            {addressList.length}
          </span>
        </div>
        <button
          type="button"
          onClick={handleAddAddress}
          data-testid="customer-address-list-add-button"
          className={`${GHOST} px-3 py-2 text-[13px]`}
        >
          <PlusIcon className="h-3.5 w-3.5" />
          Agregar Dirección
        </button>
      </div>

      <div className="flex flex-col gap-3" data-testid="customer-address-list-items">
        {addressList.map((address, index) => (
          <AddressForm
            key={address._id || index}
            address={address}
            index={index}
            onChange={(updated) => handleAddressChange(index, updated)}
            onRemove={addressList.length > 1 ? () => handleRemoveAddress(index) : undefined}
          />
        ))}
      </div>

      {addressList.length === 0 && (
        <div className={`${DROPZONE} cursor-default py-8`}>
          <p className="text-[14px] text-secondary-500 dark:text-secondary-400">
            No hay direcciones agregadas
          </p>
          <button
            type="button"
            onClick={handleAddAddress}
            data-testid="customer-address-list-add-first-button"
            className={`${CTA} mt-4`}
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Agregar Primera Dirección
          </button>
        </div>
      )}
    </div>
  );
}
